import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from "axios";

import { currentLocale } from "src/i18n/hosts";

// URL API берём из окружения сборки (REACT_APP_API_URL); по умолчанию — ОТНОСИТЕЛЬНЫЙ путь.
// Один и тот же билд крутится на двух доменах (ru и en) с общим бэкендом, поэтому
// абсолютный адрес зашивать нельзя: en-домен ходил бы на ru-домен (CORS + чужие куки).
// same-origin: nginx каждого домена проксирует /api/ на общий бэкенд.
// Для локального запуска задаётся в .env: REACT_APP_API_URL=http://localhost:8001/api/v1/
const baseURL = process.env.REACT_APP_API_URL || "/api/v1/";

export const getAccessToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");
export const saveAccessToken = (token: string) =>
  localStorage.setItem("accessToken", token);
export const saveRefreshToken = (token: string) =>
  localStorage.setItem("refreshToken", token);
export const removeAccessToken = () => localStorage.removeItem("accessToken");
export const removeRefreshToken = () => localStorage.removeItem("refreshToken");

export const getTgAccessToken = () => localStorage.getItem("tgAccessToken");
export const saveTgAccessToken = (token: string) =>
  localStorage.setItem("tgAccessToken", token);
export const removeTgAccessToken = () =>
  localStorage.removeItem("tgAccessToken");

// Ограничение по времени: без него запрос к недоступному бэкенду висит, пока
// браузер сам не оборвёт соединение, и интерфейс всё это время «думает».
export const baseInstance = axios.create({ baseURL, timeout: 30000 });

type AuthMode = "tg" | "browser";

const detectAuthMode = (): AuthMode => {
  const tg = (window as any)?.Telegram?.WebApp;
  const initData: string = tg?.initData ?? "";

  if (tg && initData.trim().length > 0) {
    return "tg";
  }

  // Скрипт Telegram подключён на всех страницах, поэтому сам по себе он ещё не
  // означает запуск из мессенджера — отличаем по данным запуска. Но Telegram
  // отдаёт их не сразу, и в эти секунды запросы уходили без токена (интерфейс
  // выглядел как разлогиненный). Если вход через Telegram уже выполнен и
  // обычного входа нет — продолжаем работать с его токеном.
  if (getTgAccessToken() && !getAccessToken()) {
    return "tg";
  }

  return "browser";
};

baseInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (originalRequest?.skipAuthInterceptor) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      try {
        removeAccessToken();
        removeRefreshToken();
        removeTgAccessToken();
        if (window.location.pathname === "/") {
          window.location.reload();
        } else {
          window.location.href = "/";
        }
      } catch (logoutError) {
        console.error("Logout error:", logoutError);
      }
    }

    return Promise.reject(error);
  }
);

baseInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const mode = detectAuthMode();
    const token = mode === "tg" ? getTgAccessToken() : getAccessToken();

    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    if (token) {
      (config.headers as AxiosHeaders).set("Authorization", `Bearer ${token}`);
    } else {
      (config.headers as AxiosHeaders).delete("Authorization");
    }

    // Язык версии сайта (определяется доменом, см. src/i18n/hosts.ts).
    // На бэкенде LocaleMiddleware читает Accept-Language и сериализаторы отдают
    // переведённые поля (события/исходы/фильтры) на языке запроса.
    (config.headers as AxiosHeaders).set("Accept-Language", currentLocale);

    return config;
  }
);
